import React, { useState } from 'react';
import { CAlert, CForm, CFormGroup, CInput, CInputGroup, CInputGroupAppend, CInputGroupText } from '@coreui/react';
import classNames from 'classnames';
import { useFormik } from 'formik';
import { get } from 'lodash';
import * as Yup from 'yup';
import CIcon from '@coreui/icons-react';
import LoadingButton from '../../shared/components/LoadingButton';
import { SetupApiOptions } from '../../shared/models/setup';
import SetupService from '../services/setup.service';
import eye from '../../assets/images/eye.svg';
import eyeStricken from '../../assets/images/eye-stricken.svg';

const validationSchema = Yup.object({
  password: Yup.string().matches(/^(?=.*[A-Z])(?=.*[0-9])(?=.{8,})/, 'Must contain 8 characters, at least 1 uppercase, 1 number').required("Password is required"),
  confirm_password: Yup.string()
    .required('Confirm password is required')
    .oneOf([Yup.ref('password'), null], 'Passwords must match'),
})

const initialValues: SetupApiOptions & {confirm_password: string} = {
  username: '',
  password: '',
  confirm_password: '',
};

export interface SetupFormProps {
  token: string;
  setupComplete: () => void;
}

const SetupForm = ({ token, setupComplete }: SetupFormProps) => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(undefined);
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);

  initialValues.username = atob(token);

  const onSubmit = (values: SetupApiOptions & {confirm_password: string}) => {
    setLoading(true);

    SetupService.setPassword(values)
      .then(() => {
        setLoading(false);
        setupComplete();
      })
      .catch((err: Error) => {
        setLoading(false);
        setError(err.message || 'Unknown error has occurred.');
      });
  };

  const formik = useFormik({ initialValues, validationSchema, onSubmit });
  const isInvalid = (fieldName: string) => get(formik.touched, fieldName) && get(formik.errors, fieldName);

  return (
    <CForm onSubmit={ formik.handleSubmit }>

      <div className='text-center'>
        <h3>Welcome to Glympse Pro!</h3>

        <p>
          Please set a password to complete your signup for <strong>{ initialValues.username }</strong>.
        </p>
      </div>

      <CFormGroup>
        <CInputGroup>
          <CInput
            id="password"
            type={showPassword ? 'text' : 'password'}
            name="password"
            placeholder="Password"
            className={classNames({'is-invalid': isInvalid('password')})}
            { ...formik.getFieldProps('password') }
          />
          <CInputGroupAppend>
            <CInputGroupText onClick={() => setShowPassword(!showPassword)} className="cursor-pointer">
              <CIcon src={showPassword ? eye : eyeStricken} className="password-eye"/>
            </CInputGroupText>
          </CInputGroupAppend>
          {isInvalid('password') && <div className="invalid-feedback">{ formik.errors.password }</div>}
        </CInputGroup>
      </CFormGroup>

      <CFormGroup>
        <CInputGroup>
          <CInput
            id="confirm_password"
            type={showConfirmPassword ? 'text' : 'password'}
            name="confirm_password"
            placeholder="Confirm Password"
            className={classNames({'is-invalid': isInvalid('confirm_password')})}
            { ...formik.getFieldProps('confirm_password') }
          />
          <CInputGroupAppend>
            <CInputGroupText onClick={() => setShowConfirmPassword(!showConfirmPassword)} className="cursor-pointer">
              <CIcon src={showConfirmPassword ? eye : eyeStricken} className="password-eye"/>
            </CInputGroupText>
          </CInputGroupAppend>
          {isInvalid('confirm_password') && <div className="invalid-feedback">{ formik.errors.confirm_password }</div>}
        </CInputGroup>
      </CFormGroup>

      <LoadingButton type="submit" color="info" block isLoading={ loading }>
        Set Password
      </LoadingButton>
      {
        error ? (
          <CAlert className="my-10 mt-3 mb-0" color="warning" fade>{ error }</CAlert>
        ) : null
      }
    </CForm>
  );
};

export default SetupForm;
